import React, { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { Plus, Minus, MessageCircle, ArrowRight } from "lucide-react";
import { COMPANY, PORTFOLIO } from "../content";

const SERVICES = [
  {
    name: "Civil works",
    desc: "Structural changes, partition walls, demolition, plastering, waterproofing — handled by our own civil team without subcontracting risk.",
  },
  {
    name: "MEP (Electrical, Plumbing, HVAC)",
    desc: "Full mechanical, electrical and plumbing coordination — from new circuits and plumbing routes to AC re-zoning and ventilation upgrades.",
  },
  {
    name: "Joinery & custom furniture",
    desc: "Wardrobes, kitchens, vanities, beds, headboards, wall panels and TV units — designed and manufactured in our own UAE workshop.",
  },
  {
    name: "Full interior finishing",
    desc: "Flooring, ceilings, painting, lighting, curtains, upholstery and final styling — every surface finished to handover standard.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Site visit & brief",
    body: "We meet you on site, understand the scope, take measurements and listen to how the space needs to work for you.",
  },
  {
    n: "02",
    title: "Design & costing",
    body: "Concept layouts, material selections and a transparent itemised quote — so you know exactly what you're paying for before anything starts.",
  },
  {
    n: "03",
    title: "Approvals & mobilisation",
    body: "We prepare the building approvals, secure access, mobilise our team and protect existing finishes before work begins.",
  },
  {
    n: "04",
    title: "Execution under one roof",
    body: "Civil, MEP, joinery and finishing happen in coordinated phases — managed by a single project lead, not a chain of subcontractors.",
  },
  {
    n: "05",
    title: "Snag, handover, support",
    body: "Final snag list, deep clean, walkthrough and a defects warranty period — we stay accountable after handover, not just before.",
  },
];

const FAQS = [
  {
    q: "What is turnkey fit-out?",
    a: "Turnkey fit-out means one contractor takes responsibility for the entire interior — design, civil works, MEP, joinery, furniture and final finishing — and hands over a fully completed, ready-to-use space. You sign one contract, deal with one project lead, and avoid the cost and friction of coordinating multiple specialists.",
  },
  {
    q: "Do you handle approvals?",
    a: "Yes. Our team manages the building, community and authority approvals required for fit-out and renovation work in Dubai and the wider UAE — including consultant drawings, permit applications, security deposits and access scheduling.",
  },
  {
    q: "Do you work across the UAE?",
    a: "Yes. We deliver projects across Dubai, Sharjah, Abu Dhabi and Ajman, including villas, apartments, offices, clinics and holiday homes. Our workshop and core team are UAE-based, which means same-week site visits and direct accountability.",
  },
  {
    q: "Do you provide full execution?",
    a: "Yes. From the first demolition strip-out to the final styled handover photo, every phase is delivered by our own teams — civil, MEP, joinery, furniture, upholstery, curtains and finishing. We do not subcontract the work that defines our standard.",
  },
];

const WHATSAPP = `https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`;

export default function InteriorFitOutDubai() {
  const [openFaq, setOpenFaq] = useState(0);

  usePageMeta({
    title:
      "Interior Fit-Out Company in Dubai & UAE | Turnkey Contractor — Time Furniture",
    description:
      "Turnkey interior fit-out contractor in Dubai and the UAE. Civil works, MEP, joinery, custom furniture and full finishing — delivered in-house since 1993.",
    canonical: "https://timefurniture.ae/interior-fit-out-dubai",
    ogImage: "/media/team-projects/time-furniture-finishing-installation-team.webp",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        url: "https://timefurniture.ae/interior-fit-out-dubai",
        name: "Interior Fit-Out Company in Dubai & UAE — Time Furniture",
        description:
          "Turnkey interior fit-out contractor in Dubai and the UAE — civil works, MEP, joinery, custom furniture and full finishing under one roof.",
        mainEntity: {
          "@type": "Service",
          name: "Turnkey Interior Fit-Out — Dubai & UAE",
          provider: { "@id": "https://timefurniture.ae/#organization" },
          areaServed: ["Dubai", "Sharjah", "Abu Dhabi", "United Arab Emirates"],
          serviceType: [
            "Civil works",
            "MEP (Electrical, Plumbing, HVAC)",
            "Joinery and custom furniture",
            "Full interior finishing",
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <div data-testid="ifo-dubai-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Interior Fit-Out Dubai" },
        ]}
      />

      {/* Hero */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16 md:pb-20">
        <p className="eyebrow mb-5">Interior Fit-Out · Dubai & UAE</p>
        <h1
          data-testid="ifo-h1"
          className="font-serif-display text-4xl md:text-6xl lg:text-[68px] leading-[1.05] tracking-tight text-[#1C110C]"
        >
          Interior Fit-Out Company in{" "}
          <em className="text-[#6E1F2C] not-italic">Dubai &amp; UAE</em>.
        </h1>
        <p
          data-testid="ifo-hero-subtext"
          className="mt-8 max-w-2xl text-[#3E2A22] leading-[1.7] text-base md:text-lg font-light"
        >
          Premium turnkey execution since 1993 · UAE-wide service · One contract, one accountable team.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link
            to="/contact"
            data-testid="ifo-hero-cta-quote"
            className="inline-flex items-center justify-center gap-3 bg-[#6E1F2C] text-white px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-colors"
          >
            Get Quote <ArrowRight size={14} />
          </Link>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ifo-hero-cta-whatsapp"
            className="inline-flex items-center justify-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-colors"
          >
            <MessageCircle size={14} strokeWidth={2.2} /> WhatsApp Us
          </a>
        </div>
      </section>

      {/* 1. Introduction */}
      <section
        data-testid="ifo-intro"
        className="max-w-[1100px] mx-auto px-6 md:px-12 pb-20 md:pb-28"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
          Introduction
        </h2>
        <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-3xl">
          We are a turnkey interior fit-out contractor in the UAE providing
          complete project execution from concept to completion. Since 1993,
          we&rsquo;ve handed over villas, apartments, offices, clinics and
          holiday homes across Dubai, Sharjah, Abu Dhabi and the wider
          Emirates — with civil works, MEP, joinery and finishing all
          delivered by our own team.
        </p>
      </section>

      {/* 2. Services */}
      <section
        data-testid="ifo-services"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Services we deliver in-house
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {SERVICES.map((s, i) => (
              <div
                key={s.name}
                data-testid={`ifo-service-${i}`}
                className="border-t border-[#6E1F2C]/20 pt-6"
              >
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {s.name}
                </h3>
                <p className="text-[#3E2A22] leading-[1.8] text-base">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Process */}
      <section
        data-testid="ifo-process"
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
          How we work — step by step
        </h2>
        <ol className="space-y-10">
          {STEPS.map((s) => (
            <li
              key={s.n}
              data-testid={`ifo-step-${s.n}`}
              className="grid grid-cols-12 gap-6 md:gap-10 border-t border-[#6E1F2C]/15 pt-8"
            >
              <p className="col-span-2 md:col-span-1 font-serif-display text-2xl md:text-3xl text-[#D4A963]">
                {s.n}
              </p>
              <div className="col-span-10 md:col-span-11">
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {s.title}
                </h3>
                <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-2xl">
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* 4. Why Choose Us */}
      <section
        data-testid="ifo-why"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Why choose Time Furniture
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                t: "In-house execution",
                d: "Civil, MEP, joinery, upholstery and finishing — all under one roof, no subcontracting handoffs.",
              },
              {
                t: "30+ years of experience",
                d: "Established in 1993. Three decades of UAE projects mean fewer surprises and faster decisions on site.",
              },
              {
                t: "UAE-wide projects",
                d: "Dubai, Sharjah, Abu Dhabi and Ajman — same accountable team, same standard, every emirate.",
              },
            ].map((w) => (
              <div key={w.t} className="border-t border-[#6E1F2C]/20 pt-6">
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {w.t}
                </h3>
                <p className="text-[#3E2A22] leading-[1.8] text-base">
                  {w.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4.5 · Portfolio Preview */}
      <section
        data-testid="ifo-portfolio-preview"
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-6">
          <div>
            <p className="eyebrow mb-4">Recent Projects</p>
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">
              Selected work across the UAE
            </h2>
          </div>
          <Link
            to="/portfolio"
            data-testid="ifo-portfolio-link"
            className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C]/40 pb-1 hover:border-[#6E1F2C]"
          >
            View Full Portfolio →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {PORTFOLIO.slice(0, 3).map((p, i) => (
            <Link
              key={p.id}
              to={`/portfolio/${p.id}`}
              data-testid={`ifo-portfolio-card-${i}`}
              className="group block"
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#1C110C]/5">
                <img
                  src={p.image}
                  alt={`${p.title} — ${p.location}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <p className="mt-4 text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F]">
                {p.category} · {p.year}
              </p>
              <h3 className="mt-2 font-serif-display text-lg text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">
                {p.title}
              </h3>
              <p className="mt-1 text-sm text-[#7A6B5F]">{p.location}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 5. FAQ */}
      <section
        data-testid="ifo-faq"
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <p className="eyebrow mb-5">Common Questions</p>
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
          Frequently asked
        </h2>
        <div className="border-t border-[#6E1F2C]/20">
          {FAQS.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div
                key={f.q}
                data-testid={`ifo-faq-item-${i}`}
                className="border-b border-[#6E1F2C]/20"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  data-testid={`ifo-faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-7 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif-display text-lg md:text-2xl text-[#1C110C] leading-snug">
                    {f.q}
                  </span>
                  <span className="text-[#6E1F2C] flex-shrink-0">
                    {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                {isOpen && (
                  <p className="pb-8 pr-12 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]">
                    {f.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="ifo-cta"
        className="bg-[#1C110C] text-[#FAF5EC]"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.08] text-[#FAF5EC] max-w-3xl mx-auto">
            Ready to plan your{" "}
            <em className="text-[#D4A963] not-italic">interior fit-out</em>?
          </h2>
          <p className="mt-7 max-w-xl mx-auto text-[#EDE1C8]/85 leading-[1.7] text-base md:text-lg">
            Book a free consultation. We&rsquo;ll review your scope and reply
            with an indicative timeline and budget — no pressure, no obligation.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ifo-cta-consultation"
              className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-all"
            >
              <MessageCircle size={14} strokeWidth={2.2} /> WhatsApp Us →
            </a>
            <Link
              to="/contact"
              data-testid="ifo-cta-inquiry"
              className="inline-flex items-center justify-center gap-3 border border-[#D4A963]/60 text-[#FAF5EC] px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#D4A963] hover:text-[#1C110C] hover:border-[#D4A963] transition-all"
            >
              Send Inquiry Form
            </Link>
          </div>
          <p className="mt-8 text-xs tracking-[0.04em] text-[#EDE1C8]/60">
            Crafted since 1993 · Dubai · Sharjah · Abu Dhabi · UAE
          </p>
        </div>
      </section>

      {/* Quiet inline link to existing pages */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 text-center">
        <p className="text-sm text-[#7A6B5F]">
          Also serving{" "}
          <Link
            to="/interior-fit-out-abu-dhabi"
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            Abu Dhabi
          </Link>{" "}
          and{" "}
          <Link
            to="/interior-fit-out-sharjah"
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            Sharjah
          </Link>
          . Or explore our{" "}
          <Link
            to="/services"
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            full fit-out services
          </Link>{" "}
          and{" "}
          <Link
            to="/portfolio"
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            completed interior projects
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
